// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { distanceToRingMeters, distanceToSegmentMeters, type LatLon } from './tileMath';

/**
 * The mapped boundaries a hole can be drawn from, and the rules for picking the ones that
 * belong to one hole's picture.
 *
 * Pure geometry and palettes, no React: the hole view, the admin analysis map and the
 * course-guide drawing all colour and order the same shapes, and the only way three surfaces
 * agree on what a bunker looks like is to read it from one place.
 */

/** A stored boundary: a bunker, a pond, a green surface, a fairway, a belt of trees. */
export type HoleShape = {
  id: string;
  kind: string;
  /** The outer ring, in order. */
  ring: LatLon[];
};

/**
 * The trouble a player aims around, which is what the in-round overlay draws.
 *
 * The green is in here because it is an object with an edge: knowing where the surface
 * starts and stops is the difference between a number to the middle and a club selection.
 */
export const HAZARD_KINDS = ['bunker', 'water', 'green'];

/**
 * Ground cover. Cut from the whole course at once rather than per hole, so one fairway
 * component routinely spans two holes and a tree belt runs the length of a stretch.
 */
export const COVER_KINDS = ['fairway', 'trees'];

/** Draw order. Cover goes down first, then hazards, then the green on top of both. */
export const SHAPE_DEPTH: Record<string, number> = {
  trees: 0,
  fairway: 1,
  water: 2,
  bunker: 3,
  green: 4,
};

/**
 * Boundary colours drawn over satellite imagery: a stroke with the same colour behind it at
 * low opacity, bright enough to read against grass, water and sand alike.
 */
export const OVERLAY_COLORS: Record<string, string> = {
  bunker: '#F0C450',
  water: '#3884FF',
  green: '#3CC86E',
  fairway: '#9BE87A',
  trees: '#1F6B3A',
};

/** Flat cartographic fills, in the spirit of a paper yardage book rather than the imagery. */
export const DRAWING_FILLS: Record<string, { fill: string; stroke: string }> = {
  trees: { fill: '#1E3A24', stroke: '#16281A' },
  fairway: { fill: '#4E8C46', stroke: '#3F7139' },
  water: { fill: '#2C6FA8', stroke: '#1E4F79' },
  bunker: { fill: '#D9C58E', stroke: '#B39F68' },
  green: { fill: '#7FC46A', stroke: '#5B9A4B' },
};

/** What a drawing shows where nothing else is mapped: everything is rough until proven otherwise. */
export const ROUGH_FILL = '#2F5B33';

/**
 * How far outside the tees and the green a boundary may sit and still belong to the hole.
 *
 * Wide enough for a greenside bunker, the pond short of the green and the trees down either
 * side; narrow enough that the next hole's fairway does not paint itself over this one.
 */
export const HOLE_FRAME_MARGIN_METERS = 80;

const METERS_PER_DEGREE_LAT = 111320;

const isFinitePoint = (point: LatLon | null | undefined): point is LatLon =>
  !!point && Number.isFinite(point.lat) && Number.isFinite(point.lon);

type Box = { minLat: number; maxLat: number; minLon: number; maxLon: number };

const boxOf = (points: LatLon[]): Box | null => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const point of points) {
    if (!isFinitePoint(point)) continue;
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLon = Math.min(minLon, point.lon);
    maxLon = Math.max(maxLon, point.lon);
  }
  return minLat === Infinity ? null : { minLat, maxLat, minLon, maxLon };
};

const overlaps = (a: Box, b: Box): boolean =>
  a.maxLat >= b.minLat && a.minLat <= b.maxLat && a.maxLon >= b.minLon && a.minLon <= b.maxLon;

/**
 * The ground a ring encloses, in square metres.
 *
 * The shoelace formula on a local equirectangular projection: over a bunker or a green the
 * curvature of the earth is far below the precision of the boundary itself, and the numbers
 * this produces are read as "is that plausible for a bunker", not surveyed.
 *
 * Zero for anything that is not a closed shape, so a stray two-point row reads as nothing
 * rather than as a sliver.
 */
export const ringAreaSquareMeters = (ring: LatLon[]): number => {
  const points = ring.filter(isFinitePoint);
  if (points.length < 3) return 0;

  const midLat = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const lonScale = Math.cos((midLat * Math.PI) / 180) * METERS_PER_DEGREE_LAT;

  let twiceArea = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    // Wraps to the first point, which closes a ring that was not stored closed.
    const next = points[(index + 1) % points.length];
    const x1 = current.lon * lonScale;
    const y1 = current.lat * METERS_PER_DEGREE_LAT;
    const x2 = next.lon * lonScale;
    const y2 = next.lat * METERS_PER_DEGREE_LAT;
    twiceArea += x1 * y2 - x2 * y1;
  }

  // Absolute: a ring stored clockwise encloses the same ground as one stored the other way.
  return Math.abs(twiceArea) / 2;
};

/**
 * A representative point inside a ring, for anchoring a label — not a true polygon centroid.
 *
 * The plain average of the vertices. For the compact, roughly-convex shapes this draws (a
 * bunker, a pond) that lands inside the shape just as reliably as an area-weighted centroid
 * would, and a ring irregular enough that the two diverge is irregular enough that a label
 * position is the least of its problems.
 */
export const ringCentroid = (ring: LatLon[]): LatLon | null => {
  const points = ring.filter(isFinitePoint);
  if (points.length === 0) return null;
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lon: points.reduce((sum, point) => sum + point.lon, 0) / points.length,
  };
};

/**
 * The boundaries that reach into one hole's frame, in draw order.
 *
 * Selected by geometry rather than by the hole number the row carries. Cover classes are
 * kept when their bounding box reaches the padded frame: a fairway component spanning two
 * holes lands in one of them, and filtering on the number alone leaves its neighbour with no
 * fairway at all, so the more forgiving box test is right there. A hazard is judged by real
 * distance to the tee-to-green corridor instead — a large or elongated pond can have a
 * bounding box that grazes the frame while every metre of its actual outline sits well clear
 * of the hole, which a box-overlap test cannot tell apart from a hazard genuinely greenside.
 * Distance to the corridor rather than to the tee and green points alone, the same reason the
 * OSM import attributes by centreline: a hazard sitting beside the middle of the fairway is
 * nowhere near either endpoint.
 *
 * A hole also has exactly one green. Where a neighbour's green boundary still lands inside
 * the margin — short holes running back to back — only the one closest to the green position
 * already resolved for this hole survives; the rest are dropped even though they passed the
 * distance test, the same way a second green *point* on this hole is never trusted over the
 * first (see `useHoleViewGeometry`).
 */
export const shapesNearHole = (
  shapes: HoleShape[],
  tees: LatLon[],
  green: LatLon | null,
  marginMeters: number = HOLE_FRAME_MARGIN_METERS
): HoleShape[] => {
  const anchors = [...tees, ...(green ? [green] : [])].filter(isFinitePoint);
  const holeBox = boxOf(anchors);
  // With nothing to measure against there is no frame, and every polygon on the course
  // would qualify. Better to draw none than to paint the neighbouring holes over this one.
  if (!holeBox) return [];

  const latMargin = marginMeters / METERS_PER_DEGREE_LAT;
  const midLat = (holeBox.minLat + holeBox.maxLat) / 2;
  const cosLat = Math.cos((midLat * Math.PI) / 180);
  // Near the poles the longitude margin would blow up; the hole itself is the sane fallback.
  const lonMargin = Math.abs(cosLat) > 1e-6 ? latMargin / Math.abs(cosLat) : latMargin;

  const frame: Box = {
    minLat: holeBox.minLat - latMargin,
    maxLat: holeBox.maxLat + latMargin,
    minLon: holeBox.minLon - lonMargin,
    maxLon: holeBox.maxLon + lonMargin,
  };

  // One segment per tee (there is usually just one), each running to the green — the
  // corridor a hazard is judged against. Without a green there is no corridor to measure
  // along, so the fallback below is distance to the tee points themselves.
  const corridors: Array<[LatLon, LatLon]> = green
    ? tees.filter(isFinitePoint).map((tee) => [tee, green])
    : [];

  const withinFrame = shapes.filter((shape) => {
    // Two vertices are a line, not a boundary; drawing one is a stray stroke on the map.
    if (shape.ring.length < 3) return false;
    if (HAZARD_KINDS.includes(shape.kind)) {
      if (corridors.length > 0) {
        return corridors.some(([from, to]) =>
          shape.ring.some(
            (vertex) =>
              isFinitePoint(vertex) && distanceToSegmentMeters(vertex, from, to) <= marginMeters
          )
        );
      }
      return anchors.some((anchor) => distanceToRingMeters(anchor, shape.ring) <= marginMeters);
    }
    const box = boxOf(shape.ring);
    return box !== null && overlaps(box, frame);
  });

  let closestGreenId: string | null = null;
  if (green) {
    let closestDistance = Infinity;
    for (const shape of withinFrame) {
      if (shape.kind !== 'green') continue;
      const distance = distanceToRingMeters(green, shape.ring);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestGreenId = shape.id;
      }
    }
  }

  return withinFrame
    .filter((shape) => shape.kind !== 'green' || shape.id === closestGreenId)
    .sort((a, b) => (SHAPE_DEPTH[a.kind] ?? 9) - (SHAPE_DEPTH[b.kind] ?? 9));
};
